import React from "react";

function WebhookReference({ apiInfo }) {
  if (!apiInfo) return null;

  return (
    <div className="webhook-reference">
      <section className="doc-section">
        <h2>Webhooks Overview</h2>
        <p>
          The CRM pushes events to a callback URL that you register. Every
          request is signed with HMAC-SHA256 so you can verify authenticity.
        </p>
      </section>

      <section className="doc-section">
        <h2>Event Types</h2>
        <ul>
          {apiInfo.events.map((ev) => (
            <li key={ev}>
              <code>{ev}</code>
            </li>
          ))}
        </ul>
      </section>

      <section className="doc-section">
        <h2>Payload Structure</h2>
        <pre className="doc-code">
          <code>{`{
  "event_id": "evt_20260920_143022_abc",
  "event_type": "lead.status_changed",
  "event_version": "1.0",
  "occurred_at": "2026-09-20T14:30:22Z",
  "subscription_id": "sub_20260915_xyz",
  "lead": {
    "external_source": "partner_xyz",
    "external_id": "lead-12345",
    "lead_id": 583,
    "company": "123 Ford",
    "status": "Qualified",
    "status_previous": "Contacted",
    "source": "DFC",
    "assigned_rep_id": 13,
    "updated_at": "2026-09-20T14:30:22Z"
  },
  "changed_fields": ["status"],
  "actor": {
    "type": "user",
    "user_id": 13,
    "name": "Gary Mitchell"
  }
}`}</code>
        </pre>
      </section>

      <section className="doc-section">
        <h2>Signature Verification</h2>
        <p>
          Every webhook includes an <code>X-IVSG-Signature</code> header in the
          format <code>t=&lt;timestamp&gt;,v1=&lt;hex&gt;</code>. The signed
          string is <code>timestamp.rawBody</code>.
        </p>
        <pre className="doc-code">
          <code>{`// Node.js example
const crypto = require("crypto");

function verifyWebhook(secret, signatureHeader, rawBody) {
  const parts = {};
  for (const item of signatureHeader.split(",")) {
    const idx = item.indexOf("=");
    if (idx > 0) parts[item.slice(0, idx)] = item.slice(idx + 1);
  }
  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`)
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(parts.v1)
  );
}`}</code>
        </pre>
      </section>

      <section className="doc-section">
        <h2>Retry Policy</h2>
        <p>
          Failed deliveries are retried on this schedule (configurable):
        </p>
        <ol>
          <li>Immediate</li>
          <li>+1 minute</li>
          <li>+5 minutes</li>
          <li>+30 minutes</li>
          <li>+2 hours</li>
          <li>+6 hours</li>
          <li>+24 hours</li>
          <li>Give up — moved to dead letter</li>
        </ol>
      </section>

      <section className="doc-section">
        <h2>Token Renewal</h2>
        <p>
          Subscription access tokens expire after 30 days by default. The CRM
          sends a <code>token_expiring_soon</code> event 7 days before expiry.
          Call <code>POST /api/external/subscriptions/:id/renew</code> with
          your API key to receive a fresh token.
        </p>
      </section>
    </div>
  );
}

export default WebhookReference;